import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { EditTask, Task, TaskConfigField, TaskFieldContent, TaskLabel } from '@app/interfaces/lm/kanban-board';
import { Account } from '@app/interfaces/sys/account';
import { SelectOption } from '@app/interfaces/sys/dict';
import { KanbanBoardService } from '@services/lm/kanban-board.service';
import { SettingsService } from '@services/sys/settings.service';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { LabelSelectComponent } from '../label-select/label-select.component';
import { AssignMemberComponent } from '../assign-member/assign-member.component';
import { DateSelectComponent } from '../date-select/date-select.component';
import { ZSelectComponent } from '../z-select/z-select.component';

@Component({
    selector: 'app-field-item',
    templateUrl: './field-item.component.html',
    styleUrl: './field-item.component.less',
})
export class FieldItemComponent implements OnChanges, OnInit, OnDestroy {
    @Input() field!: TaskConfigField;
    @Input() taskInfo!: Task;
    @Input() projectMembers: SelectOption[] = [];
    @Input() accountList: Account[] = [];
    @Input() templateAllFields: TaskConfigField[] = [];
    @Output() saveField = new EventEmitter();
    @Output() updateInactiveUsers = new EventEmitter();
    @Output() taskInfoChange = new EventEmitter<Task>();
    @ViewChildren(NzSelectComponent) selects!: QueryList<NzSelectComponent>;
    @ViewChildren(NzDatePickerComponent) datePickers!: QueryList<NzDatePickerComponent>;
    @ViewChildren(LabelSelectComponent) labelSelects!: QueryList<LabelSelectComponent>;
    @ViewChildren(AssignMemberComponent) assignMembers!: QueryList<AssignMemberComponent>;
    @ViewChildren(DateSelectComponent) dateSelects!: QueryList<DateSelectComponent>;
    @ViewChildren(ZSelectComponent) zSelects!: QueryList<ZSelectComponent>;
    submitLoading = false;

    currentValue!: any;
    value: any;
    inactiveUsers: boolean = false;
    options: any[] = [];
    selectIds: any[] = [];
    timer!: number | null;
    fieldKey = '';
    showGantt: { category: boolean } = { category: false };

    get categoryName(): string {
        return this.field.categoryName || '';
    }

    get isDefaultField(): boolean {
        return ['labels', 'assignees'].includes(this.fieldKey);
    }

    constructor(private kanbanBoardService: KanbanBoardService,
                private settingService: SettingsService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.taskInfo) {
            this.refreshTask();
        }
        // Recalculate inactive users when taskInfo or projectMembers change
        if ((changes['taskInfo'] || changes['projectMembers']) && this.taskInfo && this.projectMembers?.length > 0) {
            this.updateInactiveMembers();
        }
    }

    ngOnDestroy(): void {
        this.clearTimer();
    }

    getGanttChart(categoryName: string) {
      this.showGantt = { ...this.showGantt, category: categoryName === 'Tester' || categoryName === 'Resource'};
    }

    updateInactiveMembers() {
      const taskInfo = this.taskInfo;
      const assigneeIds = taskInfo.assignees || [];
      const otherUserFields = (this.taskInfo.taskContentResponses || []).filter(fields => fields.category === 'User');
      const otherUserFieldInactiveIds = otherUserFields.flatMap(field => field.value);

      const allTaskUserIds = new Set([...assigneeIds, ...otherUserFieldInactiveIds]);
      const activeMembers = this.projectMembers.flatMap(members => members.value.toString());
      const hasInactiveTesters = this.updateInactiveTesters();
      const hasInactiveUser = !Array.from(allTaskUserIds).every(assignee => activeMembers.includes(assignee.toString())) || hasInactiveTesters;
      this.inactiveUsers = hasInactiveUser;
      this.updateInactiveUsers.emit(hasInactiveUser);
    }

    updateInactiveTesters(): boolean {
        const testerInfo = this.taskInfo.testersInfo?.flatMap(tester => {
            return {
                id: tester.value,
                name: tester.label,
                isActive: (tester.avatar as any)?.enable
            }
        }) || [];
        return testerInfo.some(tester => !tester.isActive);
    }

    clearTimer(): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    refreshTask(): void {
        this.clearTimer();
        this.timer = window.setTimeout(() => {
            this.initValue();
        }, 350);
    }

    ngOnInit(): void {
        this.initValue();
        this.updateInactiveMembers();
    }

    initValue(): void {
        const { defaultValueRule, categoryIsPlural, isRestrained } = this.field;
        this.options = this.field?.options || [];
        const emptyValue = categoryIsPlural ? [] : '';
        const defaultFieldEmptyValue = isRestrained ? '' : [];
        if (this.kanbanBoardService.isDefaultField(defaultValueRule)) {
            try {
                const config = defaultValueRule ? JSON.parse(defaultValueRule) : {};
                const fieldName = config.fieldKey;
                this.fieldKey = fieldName;
                this.currentValue = this.taskInfo?.[fieldName as keyof Task] || defaultFieldEmptyValue;
                if (this.fieldKey === 'priority') {
                    this.options = this.kanbanBoardService.getTaskPriorityOptions().map(it => it.value);
                }

                if (this.fieldKey === 'labels') {
                    const value = (this.taskInfo?.[fieldName as keyof Task] || []) as TaskLabel[];
                    this.currentValue = value.map(it => it.id);
                }
            } catch (e) {
            }
        } else if (this.field?.categoryName === 'Resource') {
            this.currentValue = this.taskInfo.resources || [];
        } else if (this.field?.categoryName === 'Tester') {
            this.currentValue = this.taskInfo.testers || [];
        } else {
            const taskContentResponses = this.taskInfo?.taskContentResponses || [];
            const currentOption = taskContentResponses.find(it => it.taskConfigFieldId === this.field.id);
            if (!currentOption) {
                this.currentValue = emptyValue;
                return;
            }
            this.currentValue = currentOption.value || emptyValue;
        }
    }

    saveEditorValue(data: {
        value: string,
        newFiles: string[]
    }): void {
        const { value } = data;
        this.saveFieldValue(value);
    }

    async saveFieldValue(value: any, field?: TaskConfigField): Promise<void> {
        try {
            this.submitLoading = true;
            const { parentTaskId, taskContentResponses } = this.taskInfo;
            const { defaultValueRule, fieldName, isRestrained } = this.field;
            const defaultFieldEmptyValue = isRestrained ? '' : [];
            let params = this.kanbanBoardService.getTaskParams(this.taskInfo);
            if (this.kanbanBoardService.isDefaultField(defaultValueRule)) {
                const updatedField = {
                    [this.fieldKey || fieldName]: value,
                };
                params = { ...params, ...updatedField };
            } else if (field?.categoryName === 'Resource') {
                params = {
                    ...params,
                    resources: value || defaultFieldEmptyValue
                };
            } else if (field?.categoryName === 'Tester') {
                params = {
                    ...params,
                    testers: value || []
                };
            } else {
                const taskFieldContents = this.kanbanBoardService.getTaskFieldContents(field || this.field as TaskConfigField, value, this.templateAllFields, taskContentResponses);
                params = {
                    ...params,
                    taskFieldContents,
                };
            }
            const taskDetail = parentTaskId ? await this.kanbanBoardService.editSubTask(params) :
                await this.kanbanBoardService.editTask(params);
            console.log('taskDetail', taskDetail);
            this.taskInfo = this.kanbanBoardService.formatTasks([taskDetail], this.accountList)[0];
            this.taskInfoChange.emit(this.taskInfo);
            this.initValue();
            this.saveField.emit({
                field: this.field,
                fieldKey: this.fieldKey,
                value: this.fieldKey === 'labels' ? (this.taskInfo.labels || []) : value
            });
        } finally {
            this.submitLoading = false;
            this.updateInactiveMembers();
        }
    }

    closeAllDropdowns(): void {
        this.selects?.forEach(select => select.setOpenState(false));
        this.datePickers?.forEach(picker => picker.close());
        this.labelSelects?.forEach(comp => comp.closeDropdown());
        this.assignMembers?.forEach(comp => comp.closeDropdown());
        this.dateSelects?.forEach(comp => comp.closeDropdown());
        this.zSelects?.forEach(comp => comp.closeDropdown());
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }
}
